/*
 * GameBoard.tsx
 * Core puzzle game engine component that handles tile creation, 3D flip animations,
 * state tracking, and user interactions.
 */
'use client';

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

// Config
import { GUESSES, ANIMATION, DEBUG_LOGS } from '../config/constants';

// Hooks
import { useAlbumImage } from './useAlbumImage';
import { useCustomCursor, CustomCursor } from '../effects/CustomCursor';
import { useViewSize } from '@/providers/ViewSizeContext';

// Components
import GameInput from '../GameInput/GameInput';
import GameMeter from '../GameMeter/GameMeter';

// Debug
import { useDebug } from "@/utils/useDebugLog";

// Styles
import styles from './GameBoard.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// --------------------

interface Tile {
  id: number;
  row: number;
  col: number;
  x: number;
  y: number;
  width: number;
  height: number;
  isRevealed: boolean;
  isFlipping: boolean;
}

interface GameBoardProps {
  canvasSize?: number;
  gridSize?: number;
  imageSrc?: string;
  correctAnswer?: string;
  onGameStateUpdate?: (revealedCount: number, totalTiles: number) => void;
  onGuessUsed?: (guessesUsed: number, totalGuesses: number) => void;
  onGuessMeterUpdate?: (guessesUsed: number, incorrectGuesses: number[]) => void;
  onGameOver?: (reason: 'won' | 'out-of-guesses', playerSubmitted?: boolean) => void;
  onGetRemainingTiles?: (getRemainingTilesCallback: () => number[]) => void;
  onGetFlipTileFunction?: (flipTileCallback: (tileId: number) => void) => void;
  onGetTilesMap?: (getTilesMapCallback: () => Map<number, { wrapper: HTMLDivElement }>) => void;
  onGetwrapperGameInputRef?: (ref: HTMLDivElement | null) => void;
  onGetwrapperGameMeterRef?: (ref: HTMLDivElement | null) => void;
  onTileFlipped?: (tileId: number) => void;
  onTriggerGameOver?: (getTriggerGameOverCallback: (reason: 'won' | 'out-of-guesses') => void) => void;
  // Restored state from localStorage
  initialGuessesUsed?: number;
  initialIncorrectGuesses?: number[];
  copy?: {
    gameInput: {
      placeholderGuessAlbum: string;
      clearButtonAriaLabel: string;
      submitButtonAriaLabel: string;
      incorrectGuess: string;
    };
    gameMeter?: {
      singular?: string;
      plural?: string;
    };
    finalGuessToast?: {
      message: string;
    };
  };
}

const GameBoard: React.FC<GameBoardProps> = ({
  canvasSize = 600,
  gridSize = 6,
  imageSrc = '/test-album.jpg',
  correctAnswer = 'Answer',
  onGameStateUpdate,
  onGuessUsed,
  onGuessMeterUpdate,
  onGameOver,
  onGetRemainingTiles,
  onGetFlipTileFunction,
  onGetTilesMap,
  onGetwrapperGameInputRef,
  onGetwrapperGameMeterRef,
  onTileFlipped,
  onTriggerGameOver,
  initialGuessesUsed = 0,
  initialIncorrectGuesses = [],
  copy,
}) => {
  const { log } = useDebug({ name: 'GameBoard', bgColor: '#005C53', textColor: 'white', enabled: DEBUG_LOGS });

  const { isTouchDevice, isDesktop } = useViewSize();

  // Element refs
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const toastFinalGuessRef = useRef<HTMLDivElement>(null);
  const wrapperGameInputRef = useRef<HTMLDivElement>(null);
  const wrapperGameMeterRef = useRef<HTMLDivElement>(null);
  const gameBoardRef = useRef<HTMLDivElement>(null);
  const playerSubmittedRef = useRef(false);
  const tileContainerRef = useRef<HTMLDivElement>(null);
  const tilesRef = useRef<Map<number, { wrapper: HTMLDivElement; front: HTMLDivElement; back: HTMLDivElement }>>(new Map());
  const tileEventHandlersRef = useRef<Map<number, { mouseenter: () => void; mouseleave: () => void; click: () => void }>>(new Map());
  const flippedTilesRef = useRef<Set<number>>(new Set()); // Track flipped tiles persistently

  // GSAP timeline ref
  const toastTimelineRef = useRef<gsap.core.Timeline | null>(null);

  // State refs
  const isOutOfGuessesRef = useRef(initialGuessesUsed >= GUESSES); // Track if out of guesses immediately (before state updates)
  const boardDisabledRef = useRef(initialGuessesUsed >= GUESSES); // Track board disabled state immediately
  const incorrectGuessesRef = useRef<number[]>(initialIncorrectGuesses); // Track incorrect guesses immediately for closures
  const hasProvidedGameInputRef = useRef(false);
  const hasProvidedGameMeterRef = useRef(false);

  // State
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [guess, setGuess] = useState<string>('');
  const [guessesUsed, setGuessesUsed] = useState(initialGuessesUsed);
  const [incorrectGuesses, setIncorrectGuesses] = useState<number[]>(initialIncorrectGuesses); // Track which guess numbers were incorrect submissions
  const [hasFlippedTile, setHasFlippedTile] = useState(initialGuessesUsed > 0); // Track if at least one tile has been flipped
  const [showIncorrectFeedback, setShowIncorrectFeedback] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [boardDisabled, setBoardDisabled] = useState(initialGuessesUsed >= GUESSES); // Disable board immediately on last guess
  const isMountedRef = useRef(false);
  const gameWonRef = useRef(false); // Track if the game was won (vs lost)

  // Custom cursor enabled only for desktop non-touch devices
  const useCustomCursorFlag = !isTouchDevice && isDesktop;

  // Use custom hook for image loading
  const { albumImage, isLoading } = useAlbumImage({ imageSrc, canvasSize });

  // Use custom cursor hook (only when custom cursor is enabled)
  const { cursorPos, showCursor } = useCustomCursor({
    containerRef: gameBoardRef,
    isDisabled: boardDisabled || gameOver || !useCustomCursorFlag
  });

  // Animate final guess toast
  const animateFinalGuessToast = useCallback(() => {
    if (!toastFinalGuessRef.current) return;

    // Check if toast is already visible - if so, don't animate it in again
    const currentOpacity = gsap.getProperty(toastFinalGuessRef.current, 'autoAlpha');
    if (Number(currentOpacity) > 0) return;

    // Kill any existing timeline
    if (toastTimelineRef.current) {
      toastTimelineRef.current.kill();
    }

    const tl = gsap.timeline();
    toastTimelineRef.current = tl;

    // Set initial state (ensure it's completely hidden)
    gsap.set(toastFinalGuessRef.current, {
      autoAlpha: 0,
      y: -20,
    });

    // Fade in
    tl.to(toastFinalGuessRef.current, {
      autoAlpha: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out',
    });

    // Hold
    tl.to({}, { duration: 2.5 });

    // Fade out
    tl.to(toastFinalGuessRef.current, {
      autoAlpha: 0,
      y: -20,
      duration: 0.3,
      ease: 'power2.in',
    });
  }, []);

  // Handle click on disabled game board (remind user to use input)
  const handleBoardClick = useCallback(() => {
    if (boardDisabled && !gameOver) {
      log('User clicked on disabled board - showing final guess toast reminder');
      animateFinalGuessToast();
    }
  }, [boardDisabled, gameOver, animateFinalGuessToast]);

  // Initialize tiles
  const initializeTiles = useCallback(() => {
    const newTiles: Tile[] = [];
    const tileSize = canvasSize / gridSize;

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        newTiles.push({
          id: row * gridSize + col,
          row,
          col,
          x: col * tileSize,
          y: row * tileSize,
          width: tileSize,
          height: tileSize,
          isRevealed: false,
          isFlipping: false,
        });
      }
    }

    log('Tiles initialized:', { gridSize, totalTiles: newTiles.length });
    setTiles(newTiles);
  }, [canvasSize, gridSize]);

  // Initialize tiles on mount
  useEffect(() => {
    log('GameBoard mounted - initializing');
    initializeTiles();
    flippedTilesRef.current.clear();
    // Don't reset these if they were initialized from restored state
    // (initialGuessesUsed >= GUESSES means user is out of guesses)
    if (initialGuessesUsed < GUESSES) {
      isOutOfGuessesRef.current = false;
      boardDisabledRef.current = false;
      setBoardDisabled(false);
    }
  }, [initializeTiles, initialGuessesUsed]);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Show final guess toast if user is out of guesses on mount (restored state)
  useEffect(() => {
    if (isMountedRef.current && initialGuessesUsed >= GUESSES) {
      log('User is out of guesses on mount - showing final guess toast');
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        animateFinalGuessToast();
      }, 500);
    }
  }, [initialGuessesUsed, animateFinalGuessToast]);

  // Update parent with initial stats when tiles are first created
  useEffect(() => {
    if (tiles.length > 0 && isMountedRef.current) {
      const totalTiles = gridSize * gridSize;
      onGameStateUpdate?.(0, totalTiles);
    }
  }, [tiles.length, gridSize]);

  // Notify parent when guesses are used
  useEffect(() => {
    if (isMountedRef.current && guessesUsed > 0) {
      onGuessUsed?.(guessesUsed, GUESSES);
    }
  }, [guessesUsed, onGuessUsed]);

  // Notify parent when guess meter updates (tile flips or incorrect submissions)
  useEffect(() => {
    if (isMountedRef.current && guessesUsed > 0) {
      onGuessMeterUpdate?.(guessesUsed, incorrectGuesses);
    }
  }, [guessesUsed, incorrectGuesses, onGuessMeterUpdate]);

  // Notify parent when tiles are revealed
  useEffect(() => {
    if (isMountedRef.current && flippedTilesRef.current.size > 0) {
      const newRevealedCount = flippedTilesRef.current.size;
      const totalTiles = gridSize * gridSize;
      onGameStateUpdate?.(newRevealedCount, totalTiles);
    }
  }, [tiles, gridSize, onGameStateUpdate]);

  // Notify parent when game is over
  useEffect(() => {
    if (gameOver && isMountedRef.current) {
      // Determine if they won or lost
      // Check gameWonRef first, then fall back to isOutOfGuessesRef
      const reason = gameWonRef.current ? 'won' : 'out-of-guesses';
      log('Triggering onGameOver with reason:', reason);
      onGameOver?.(reason, playerSubmittedRef.current);

      // Hide the canvas immediately when game over
      if (canvasRef.current) {
        canvasRef.current.style.visibility = 'hidden';
      }
    }
  }, [gameOver, onGameOver]);

  // Expose getRemainingTiles function to parent
  useEffect(() => {
    if (onGetRemainingTiles) {
      const getRemainingTiles = () => {
        const allTileIds = tiles.map(t => t.id);
        const flippedIds = Array.from(flippedTilesRef.current);
        const remaining = allTileIds.filter(id => !flippedTilesRef.current.has(id));
        log('getRemainingTiles called:', {
          totalTiles: allTileIds.length,
          flipped: flippedIds,
          remaining: remaining
        });
        return remaining;
      };
      onGetRemainingTiles(getRemainingTiles);
    }
  }, [tiles, onGetRemainingTiles]);

  // Expose flipTile function to parent
  useEffect(() => {
    if (onGetFlipTileFunction) {
      const flipTileForParent = (tileId: number) => {
        // log('flipTileForParent called for tile:', tileId, 'already flipped:', flippedTilesRef.current.has(tileId));

        // Skip if already flipped
        if (flippedTilesRef.current.has(tileId)) {
          log('Tile already flipped, skipping:', tileId);
          return;
        }

        const tileElements = tilesRef.current.get(tileId);
        if (!tileElements) {
          log('No tile elements found for:', tileId);
          return;
        }

        const tile = tiles.find(t => t.id === tileId);
        if (!tile) {
          log('No tile data found for:', tileId);
          return;
        }

        // Mark as flipped IMMEDIATELY before animation starts
        flippedTilesRef.current.add(tileId);
        // log('Marked tile as flipped:', tileId, 'flipped set now:', Array.from(flippedTilesRef.current));

        // Update tile state
        setTiles(prevTiles =>
          prevTiles.map(t =>
            t.id === tileId
              ? { ...t, isFlipping: true }
              : t
          )
        );

        // Perform flip animation
        gsap.to(tileElements.wrapper, {
          rotationY: 180,
          duration: ANIMATION.TILE_FLIP_DURATION / 1000,
          ease: 'power1.out',
          transformOrigin: 'center center',
          onComplete: () => {
            setTiles(prevTiles =>
              prevTiles.map(t =>
                t.id === tileId
                  ? { ...t, isRevealed: true, isFlipping: false }
                  : t
              )
            );
            // Notify parent that tile was flipped
            onTileFlipped?.(tileId);
          }
        });
      };
      onGetFlipTileFunction(flipTileForParent);
    }
  }, [tiles, onGetFlipTileFunction, onTileFlipped]);

  // Provide tiles map getter to parent
  useEffect(() => {
    if (onGetTilesMap) {
      const getTilesMapForParent = () => tilesRef.current;
      onGetTilesMap(getTilesMapForParent);
    }
  }, [onGetTilesMap]);

  // Provide GameInput ref to parent
  useEffect(() => {
    if (onGetwrapperGameInputRef && wrapperGameInputRef.current && !hasProvidedGameInputRef.current) {
      // log('useEffect: Providing wrapperGameInputRef to parent', wrapperGameInputRef.current);
      onGetwrapperGameInputRef(wrapperGameInputRef.current);
      hasProvidedGameInputRef.current = true;
    }
  });

  // Provide GameMeter ref to parent
  useEffect(() => {
    if (onGetwrapperGameMeterRef && wrapperGameMeterRef.current && !hasProvidedGameMeterRef.current) {
      // log('useEffect: Providing wrapperGameMeterRef to parent', wrapperGameMeterRef.current);
      onGetwrapperGameMeterRef(wrapperGameMeterRef.current);
      hasProvidedGameMeterRef.current = true;
    }
  });

  // Expose triggerGameOver function to parent
  useEffect(() => {
    if (onTriggerGameOver) {
      const triggerGameOverForParent = (reason: 'won' | 'out-of-guesses') => {
        log('Parent triggered game over with reason:', reason);
        if (reason === 'won') {
          gameWonRef.current = true;
        }
        setGameOver(true);
      };
      onTriggerGameOver(triggerGameOverForParent);
    }
  }, [onTriggerGameOver]);

  // Ensure game-board dimensions are always WHOLE pixels
  useEffect(() => {
    if (!rootRef.current) return;

    const roundDimensions = () => {
      const board = rootRef.current;
      if (!board) return;

      // Temporarily remove inline styles to get natural size
      board.style.width = '';

      // Get the width
      const rect = board.getBoundingClientRect();

      // Round it
      const roundedWidth = Math.floor(rect.width);

      // Apply it
      board.style.width = `${roundedWidth}px`;
    };

    // Initial round on mount
    requestAnimationFrame(() => {
      roundDimensions();
    });

    // Handle window resize
    const handleResize = () => {
      requestAnimationFrame(() => {
        roundDimensions();
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Sync visual state for revealed tiles without recreating DOM
  useEffect(() => {
    // Only sync if tiles have been created
    if (tilesRef.current.size > 0) {
      tiles.forEach((tile) => {
        const tileElements = tilesRef.current.get(tile.id);
        if (tileElements && tile.isRevealed && !flippedTilesRef.current.has(tile.id)) {
          // Tile was revealed elsewhere, sync the DOM state
          gsap.set(tileElements.wrapper, { rotationY: 180 });
          flippedTilesRef.current.add(tile.id);
        }
      });
    }
  }, [tiles]);

  // Draw empty canvas (no album image showing)
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas - keep it empty, no album image showing
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Fill with black background
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
  }, [canvasSize]);

  // Function to use a guess and check game over condition
  const useGuess = useCallback((isIncorrectSubmission = false) => {
    if (gameOver || isOutOfGuessesRef.current) return false;

    setGuessesUsed(prevGuesses => {
      const newGuessesUsed = prevGuesses + 1;
      // log('Using guess:', { guessNumber: newGuessesUsed, remaining: GUESSES - newGuessesUsed });

      // Track if this was an incorrect submission
      if (isIncorrectSubmission) {
        setIncorrectGuesses(prev => {
          const updated = [...prev, newGuessesUsed];
          incorrectGuessesRef.current = updated;
          return updated;
        });
      }

      // Check if out of guesses immediately
      if (newGuessesUsed >= GUESSES) {
        isOutOfGuessesRef.current = true;
        boardDisabledRef.current = true;
        log('Out of guesses!');
        setBoardDisabled(true);

        // Show final guess toast
        animateFinalGuessToast();

        // Remove event listeners from all unflipped tiles
        tilesRef.current.forEach((tileElements, id) => {
          if (!flippedTilesRef.current.has(id)) {
            const handlers = tileEventHandlersRef.current.get(id);
            if (handlers) {
              tileElements.front.removeEventListener('mouseenter', handlers.mouseenter);
              tileElements.front.removeEventListener('mouseleave', handlers.mouseleave);
              tileElements.front.removeEventListener('click', handlers.click);
            }
          }
        });
      }

      return newGuessesUsed;
    });

    return true;
  }, [gameOver, animateFinalGuessToast]);

  // GSAP tile flip animation with proper state tracking
  const flipTile = useCallback((tileId: number) => {
    // Check if game is over or tile is already flipped
    if (gameOver || boardDisabledRef.current || flippedTilesRef.current.has(tileId)) {
      return;
    }

    // Check if we're out of guesses before allowing the click
    if (isOutOfGuessesRef.current) {
      return;
    }

    // Use a guess for revealing a tile
    useGuess();

    const tileElements = tilesRef.current.get(tileId);
    if (!tileElements) return;

    // Find tile data
    const tile = tiles.find(t => t.id === tileId);
    if (!tile) return;

    log('Flipping tile:', { tileId, row: tile.row, col: tile.col });

    // Mark as flipped immediately to prevent multiple clicks
    flippedTilesRef.current.add(tileId);

    // Enable input after first tile flip
    if (!hasFlippedTile) {
      setHasFlippedTile(true);
    }

    // Update tile state to show flipping
    setTiles(prevTiles =>
      prevTiles.map(t =>
        t.id === tileId
          ? { ...t, isFlipping: true }
          : t
      )
    );

    // Perform 3D flip animation with GSAP
    gsap.to(tileElements.wrapper, {
      rotationY: 180,
      duration: ANIMATION.TILE_FLIP_DURATION / 1000,
      ease: 'power1.out',
      transformOrigin: 'center center',
      onComplete: () => {
        // Update ARIA label for screen readers
        tileElements.front.setAttribute('aria-label', `Tile at row ${tile.row + 1}, column ${tile.col + 1} revealed`);

        // Mark tile as revealed after flip completes
        setTiles(prevTiles =>
          prevTiles.map(t =>
            t.id === tileId
              ? { ...t, isRevealed: true, isFlipping: false }
              : t
          )
        );

        // Notify parent that tile was flipped
        onTileFlipped?.(tileId);

        // Note: Don't trigger game over here when out of guesses
        // The user gets a final chance to submit the correct answer via text input
      }
    });
  }, [tiles, gridSize, useGuess, gameOver, hasFlippedTile, onTileFlipped]);

  // Create tile elements once and manage them independently
  useEffect(() => {
    if (!tileContainerRef.current || tiles.length === 0 || !albumImage) return;

    const container = tileContainerRef.current;

    // Only create tiles if they don't exist yet
    if (tilesRef.current.size === 0) {
      // Clear existing tiles
      container.innerHTML = '';
      tilesRef.current.clear();

      tiles.forEach((tile) => {
        // Create tile wrapper for 3D transformation
        const tileWrapper = document.createElement('div');
        tileWrapper.className = cx('tile-wrapper');

        // Use CSS custom properties for dynamic positioning
        tileWrapper.style.setProperty('--tile-left', `${(tile.x / canvasSize) * 100}%`);
        tileWrapper.style.setProperty('--tile-top', `${(tile.y / canvasSize) * 100}%`);
        tileWrapper.style.setProperty('--tile-width', `${(tile.width / canvasSize) * 100}%`);
        tileWrapper.style.setProperty('--tile-height', `${(tile.height / canvasSize) * 100}%`);

        // Create front face (black tile)
        const frontFace = document.createElement('div');
        frontFace.className = cx('tile-face', 'tile-front');

        // Accessibility attributes
        frontFace.setAttribute('role', 'button');
        frontFace.setAttribute('tabIndex', '0');
        frontFace.setAttribute('aria-label', `Reveal tile at row ${tile.row + 1}, column ${tile.col + 1}`);
        frontFace.setAttribute('aria-pressed', 'false');

        // Create back face (album image portion)
        const backFace = document.createElement('div');
        backFace.className = cx('tile-face', 'tile-back');

        // Create canvas for this tile's portion of the album
        const tileCanvas = document.createElement('canvas');
        tileCanvas.width = tile.width;
        tileCanvas.height = tile.height;
        const tileCtx = tileCanvas.getContext('2d');

        // Draw the portion of the album image for this tile
        if (tileCtx) {
          tileCtx.drawImage(
            albumImage,
            tile.x, tile.y, tile.width, tile.height, // source
            0, 0, tile.width, tile.height // destination
          );
        }

        // Append canvas to back face
        backFace.appendChild(tileCanvas);

        // Assemble the tile
        tileWrapper.appendChild(frontFace);
        tileWrapper.appendChild(backFace);
        container.appendChild(tileWrapper);

        // Store reference to elements
        tilesRef.current.set(tile.id, {
          wrapper: tileWrapper,
          front: frontFace,
          back: backFace
        });

        // Set initial state for revealed tiles
        if (tile.isRevealed) {
          gsap.set(tileWrapper, { rotationY: 180 });
          flippedTilesRef.current.add(tile.id);
        }

        // -------------------- Event handlers

        // Handle mouse enter (hover effect)
        const handleMouseEnter = () => {
          // Don't show hover effect if board is disabled or game is over
          if (boardDisabledRef.current || gameOver) return;
          
          if (!tile.isFlipping && useCustomCursorFlag) {
            gsap.to(frontFace, {
              backgroundColor: 'var(--highlight-color)',
              duration: ANIMATION.TILE_HOVER_DURATION / 1000,
              ease: 'power2.out'
            });
          }
        };

        // Handle mouse leave (reset color)
        const handleMouseLeave = () => {
          // Don't animate if board is disabled or game is over
          if (boardDisabledRef.current || gameOver) return;
          
          if (!tile.isFlipping && useCustomCursorFlag) {
            gsap.to(frontFace, {
              backgroundColor: '#000',
              duration: ANIMATION.TILE_HOVER_DURATION / 1000,
              ease: 'power2.out'
            });
          }
        };

        // Handle tile click to flip
        const handleClick = () => {
          if (!flippedTilesRef.current.has(tile.id)) {
            flipTile(tile.id);
          }
        };

        // Handle keyboard interaction
        const handleKeyDown = (e: KeyboardEvent) => {
          // Prevent activation when board is disabled
          if (boardDisabledRef.current || gameOver) {
            e.preventDefault();
            return;
          }

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!flippedTilesRef.current.has(tile.id)) {
              flipTile(tile.id);
            }
          }
        };

        // Store event handlers for later removal
        tileEventHandlersRef.current.set(tile.id, {
          mouseenter: handleMouseEnter,
          mouseleave: handleMouseLeave,
          click: handleClick,
        });

        // Add event listeners to front face
        frontFace.addEventListener('mouseenter', handleMouseEnter);
        frontFace.addEventListener('mouseleave', handleMouseLeave);
        frontFace.addEventListener('click', handleClick);
        frontFace.addEventListener('keydown', handleKeyDown);
      });

      // Draw canvas background once
      drawCanvas();
    }
  }, [albumImage, drawCanvas, flipTile]);

  // Update tile states without recreating elements
  useEffect(() => {
    tiles.forEach((tile) => {
      const tileElements = tilesRef.current.get(tile.id);
      if (!tileElements) return;

      const isFlipped = flippedTilesRef.current.has(tile.id);

      // Update visual state based on flipped status
      if (isFlipped && !tile.isFlipping) {
        // Ensure flipped tiles stay revealed
        gsap.set(tileElements.wrapper, { rotationY: 180 });
        tileElements.front.style.pointerEvents = 'none';
        tileElements.front.setAttribute('aria-pressed', 'true');
        tileElements.front.setAttribute('tabIndex', '-1');
      } else if (!isFlipped && !tile.isFlipping) {
        // Ensure unflipped tiles stay unrevealed
        gsap.set(tileElements.wrapper, { rotationY: 0 });
        tileElements.front.style.pointerEvents = 'auto';
        tileElements.front.setAttribute('aria-pressed', 'false');
        // Disable tab focus when board is disabled
        tileElements.front.setAttribute('tabIndex', boardDisabled ? '-1' : '0');
      }
    });
  }, [tiles, boardDisabled]);

  // Handle guess submission
  const handleSubmit = useCallback(() => {
    if (guess.trim() && !gameOver) {
      const trimmedGuess = guess.trim();
      const isCorrect = trimmedGuess.toLowerCase() === correctAnswer.toLowerCase();
      playerSubmittedRef.current = true;

      log('Submitting guess:', {
        guess: trimmedGuess,
        guessLowercase: trimmedGuess.toLowerCase(),
        correctAnswer: correctAnswer,
        correctAnswerLowercase: correctAnswer.toLowerCase(),
        isCorrect,
        isOutOfGuesses: isOutOfGuessesRef.current
      });

      if (isCorrect) {
        setGuess('');
        log('Correct answer! Game won!');
        gameWonRef.current = true; // Mark that the game was won
        setGameOver(true);
      } else {
        // Show incorrect feedback
        setShowIncorrectFeedback(true);
        // Clear input and reset feedback after duration
        setTimeout(() => {
          setGuess('');
          setShowIncorrectFeedback(false);
        }, ANIMATION.INCORRECT_FEEDBACK_DURATION);

        // Check if this is during the final chance period (out of guesses)
        if (isOutOfGuessesRef.current) {
          // They're out of guesses - this is their final chance
          // Game over after feedback animation completes
          log('Final guess attempt made - Game lost after feedback completes!');
          setTimeout(() => {
            setGameOver(true);
          }, ANIMATION.INCORRECT_FEEDBACK_DURATION);
        } else {
          // Still have guesses left - use one and mark as incorrect submission
          useGuess(true);
        }
      }
    }
  }, [guess, gameOver, correctAnswer, useGuess]);

  // Handle Enter key in input
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  }, [handleSubmit]);

  // -------------------- Render

  if (isLoading) {
    return (
      <div className={cx('game-board-container')}>
        {/* Loading... */}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={cx('game-board-container', {
      'touch-device': isTouchDevice,
      'use-custom-cursor': useCustomCursorFlag
    })}>

      {/* Custom Cursor */}
      <CustomCursor visible={showCursor && !boardDisabled && !gameOver && useCustomCursorFlag}
        x={cursorPos.x}
        y={cursorPos.y}
      />

      {/* Game Board */}
      <div ref={gameBoardRef}
        className={cx('game-board', {
          'disabled': boardDisabled || gameOver
        })}
        onClick={handleBoardClick}
      >
        <div className={cx('canvas-wrapper', { 'fade-out': gameOver })}>
          <canvas
            ref={canvasRef}
            width={canvasSize}
            height={canvasSize}
            className={cx('background-canvas')}
          />
          <div
            ref={tileContainerRef}
            className={cx('tile-container')}
          />
        </div>

        {/* Final Guess Toast */}
        <div className={cx('toast-final-guess')} ref={toastFinalGuessRef}>
          <span className={cx('text-m')}>
            {copy?.finalGuessToast?.message || "You're out of tiles. Time to make a final guess."}
          </span>
        </div>
      </div>

      {/* Game Input */}
      <div className={cx('wrapper-game-input')} ref={wrapperGameInputRef}>
        <GameInput
          guess={guess}
          onGuessChange={setGuess}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          gameOver={gameOver}
          correctAnswer={correctAnswer}
          showIncorrectFeedback={showIncorrectFeedback}
          copy={copy?.gameInput}
        />
      </div>

      {/* Game Meter */}
      <div className={cx('wrapper-game-meter')} ref={wrapperGameMeterRef}>
        <GameMeter guessesUsed={guessesUsed} incorrectGuesses={incorrectGuesses} copy={copy?.gameMeter} />
      </div>

    </div>
  );

};

export default GameBoard;
